import { useAtomValue } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { getApiInst } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { useWebphoneState } from '~/atoms/WebphoneState'
import { useAnalytics } from '~/hooks/useAnalytics'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import WebSocketService from '~/services/WebSocketService'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'

import { FeatureFlag } from '@mangovoice/constants'
import { useFeatureFlag } from '@mangovoice/core'
import { PhoneCall, PhoneCallStatus } from '@mangovoice/types'

import {
  DESKPHONE_CONNECTED_TIMEOUT_MS,
  DESKPHONE_INGESTION_GRACE_MS,
  DESKPHONE_POST_DISCONNECT_GRACE_MS,
  registrationStatusAtom,
  RINGING_TIMEOUT_MS,
  TransportConnectionState,
  transportConnectionStateAtom,
  WATCHDOG_CHECK_INTERVAL_MS,
} from './usePhone'

// A leg UUID is a 36-char v4 UUID. mango-api's CallViewSet.get_object treats a
// 36-char path segment as a leg UUID (CallLeg.uuid → Call); anything else is
// looked up as a Call primary key, which would resolve a DIFFERENT call and
// could false-remove on its status. Guard the shape before verifying.
const LEG_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isLegUuidShaped = (value: string): boolean => LEG_UUID_REGEX.test(value)

/**
 * Decides whether a deskphone call is stuck and should be force-removed by the
 * watchdog timer. Split out from the sweep: the watchdog is the time-based net
 * for calls that never got a HANGUP; the sweep is the reconnect-triggered,
 * API-verified pass.
 *
 * `transportDisconnected` reflects whether ANY realtime socket (SIP transport
 * or chat-rtm) is currently down. A CONNECTED deskphone call is only reaped on
 * the tighter post-disconnect window once we're back — while still disconnected
 * we leave it alone, since the user may be mid-call on the handset and the
 * missing HANGUP could just be a buffered event that arrives on reconnect.
 *
 * `apiConfirmedLiveAfterGap` is set when the reconnect sweep API-verified this
 * call as still live after the gap the marker records — the lost-HANGUP
 * suspicion no longer applies, so the call falls through to the 8h net instead
 * of the grace window. Sweep verifications from before a gap don't count: the
 * hook clears them whenever a socket drops.
 */
export const isStuckDeskphoneCall = (
  call: PhoneCall,
  now: number,
  lastDisconnect: number | null,
  transportDisconnected: boolean,
  apiConfirmedLiveAfterGap = false,
): boolean => {
  if (call.callSource !== 'deskphone') return false

  const callStartMs = call.startTime?.getTime()
  const isRingingOrInitial =
    call.status === PhoneCallStatus.RINGING ||
    call.status === PhoneCallStatus.INITIAL

  if (isRingingOrInitial) {
    // No start time yet: never reap. A just-arrived call may not have its
    // startTime populated; the watchdog re-checks every interval, so it's caught
    // once startTime lands rather than yanked out from under the user now.
    if (!callStartMs) return false
    return now - callStartMs > RINGING_TIMEOUT_MS
  }

  if (call.status === PhoneCallStatus.CONNECTED) {
    if (!callStartMs) return false

    if (
      lastDisconnect &&
      lastDisconnect > callStartMs &&
      !apiConfirmedLiveAfterGap
    ) {
      // A disconnect happened during this call and the sweep could NOT confirm
      // it live. The HANGUP may have been lost, so use the tighter window — but
      // only once reconnected. While still disconnected, keep the call: the
      // missing HANGUP could just be a buffered event that arrives on reconnect.
      if (transportDisconnected) return false
      return now - lastDisconnect > DESKPHONE_POST_DISCONNECT_GRACE_MS
    }
    // While a socket stays down (e.g. a lasting SIP outage with chat-rtm still
    // healthy), a CONNECTED call with a mid-call disconnect is held above and
    // never reaches this 8h net. That's intentional — HANGUP still arrives via
    // chat-rtm, so we keep the call rather than reap a possibly-live one.
    return now - callStartMs > DESKPHONE_CONNECTED_TIMEOUT_MS
  }

  return false
}

/**
 * Automatic cleanup for orphaned deskphone calls — the watchdog force-remove
 * and the reconnect sweep. Extracted from usePhoneHub so it runs from the
 * always-mounted HybridCallManager: deskphone-only users (webphone disabled)
 * still get the safety net, not just the manual Dismiss button.
 *
 * Deskphone calls have no SIP session, so removal is atom-only (callList) — no
 * webphone plumbing is needed here. The softphone watchdog (which verifies a
 * live SIP session) stays in usePhoneHub, where a SIP client exists.
 *
 * All removal side-effects are gated behind the OrphanedCallCleanup flag; when
 * off, this hook records disconnect transitions but removes nothing.
 */
export const useOrphanedDeskphoneCleanup = () => {
  const { pbxId, pbx, sessionUser } = useUserState()
  const registrationState = useAtomValue(registrationStatusAtom)
  const transportConnectionState = useAtomValue(transportConnectionStateAtom)

  // Chat-rtm connection state. Deskphone RINGING/HANGUP arrive on this socket,
  // separate from the SIP transport, so a chat-rtm-only drop (server deploy,
  // token expiry) must also arm the safety net. Seeded from the current socket
  // state so we don't assume "connected" before the first event.
  const chatRtmService = WebSocketService.getInstance(
    pbxId ?? 0,
    pbx?.uuid ?? '',
    sessionUser?.id,
  )
  const [chatRtmConnected, setChatRtmConnected] = useState(() =>
    chatRtmService.isConnected(),
  )
  useEffect(() => {
    setChatRtmConnected(chatRtmService.isConnected())
    return chatRtmService.subscribeToConnection(setChatRtmConnected)
  }, [chatRtmService])

  const {
    phoneCalls: {
      list: { callList, removeCall },
    },
  } = useWebphoneState({
    pbxId: pbxId ?? 0,
    registrationState,
    transportConnectionState,
  })
  const { logger } = useCloudwatchLogger('Webphone')
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)

  // Tracks when a realtime socket last dropped, so the watchdog can apply the
  // tighter post-disconnect window (and the sweep knows a gap occurred). NOT
  // cleared on reconnect — calls the sweep confirms live are exempted per-call
  // via apiConfirmedLiveUuids instead, so a call the sweep could NOT confirm
  // (API error/timeout) is still reaped on the grace window rather than
  // lingering until the 8h net.
  const lastDisconnectTimestamp = useRef<number | null>(null)

  // Calls the reconnect sweep API-confirmed live after the recorded gap.
  // Cleared whenever a socket drops again: a new gap means a HANGUP could have
  // been missed since the confirmation.
  const apiConfirmedLiveUuids = useRef<Set<string>>(new Set())

  // Feature flag mirrored to a ref so the interval/effect callbacks read the
  // latest value without re-subscribing.
  const cleanupEnabledRef = useRef(false)
  const cleanupFlag = useFeatureFlag(FeatureFlag.OrphanedCallCleanup)
  cleanupEnabledRef.current = cleanupFlag.isEnabled

  // Latest values for the interval-driven watchdog (avoids stale closures). The
  // watchdog subscribes once and reads through this ref.
  const refs = useRef({ callList, removeCall, logger, track })
  refs.current = { callList, removeCall, logger, track }

  // Whether any realtime socket is currently down. Deskphone RINGING/HANGUP
  // events arrive over chat-rtm, while the SIP transport is a separate socket;
  // treat the call as "degraded" if EITHER is disconnected so a lost HANGUP on
  // one socket still arms the net. Mirrored to a ref for the interval callback.
  const transportDisconnected =
    transportConnectionState === TransportConnectionState.DISCONNECTED ||
    !chatRtmConnected
  const transportDisconnectedRef = useRef(transportDisconnected)
  transportDisconnectedRef.current = transportDisconnected

  // ---- Watchdog: time-based force-remove for stuck deskphone calls ----
  useEffect(() => {
    const checkForStuckCalls = () => {
      if (!cleanupEnabledRef.current) return

      const now = Date.now()
      const {
        callList: currentCallList,
        removeCall: currentRemoveCall,
        logger: currentLogger,
        track: currentTrack,
      } = refs.current

      for (const call of currentCallList.values()) {
        if (
          !isStuckDeskphoneCall(
            call,
            now,
            lastDisconnectTimestamp.current,
            transportDisconnectedRef.current,
            apiConfirmedLiveUuids.current.has(call.callUuid),
          )
        ) {
          continue
        }

        const hadDisconnect = !!(
          lastDisconnectTimestamp.current &&
          call.startTime &&
          lastDisconnectTimestamp.current > call.startTime.getTime()
        )
        const ageSeconds = call.startTime
          ? Math.floor((now - call.startTime.getTime()) / 1000)
          : null
        // A CONNECTED deskphone call removed here was force-removed on a timer
        // with NO backend confirmation — the one path that can reap a live
        // call. Tag it distinctly so the dashboard can alarm on short-lived
        // connected removals separately from the benign ringing-timeout case.
        const layer =
          call.status === PhoneCallStatus.CONNECTED
            ? 'watchdog_connected_timeout'
            : 'watchdog_ringing_timeout'
        currentLogger?.warn('Orphaned deskphone call removed', {
          callUuid: call.callUuid,
          status: call.status,
          number: call.number,
          callSource: call.callSource,
          ageSeconds: ageSeconds ?? 'unknown',
          hadDisconnect,
          layer,
        })
        currentTrack(WebPhoneAnalyticsEvents.OrphanedCallCleanup, {
          layer,
          callSource: 'deskphone',
          callStatus: call.status,
          hadDisconnect,
          ageSeconds,
        })
        currentRemoveCall(call.callUuid)
      }
    }

    const intervalId = setInterval(
      checkForStuckCalls,
      WATCHDOG_CHECK_INTERVAL_MS,
    )
    checkForStuckCalls() // Run immediately on mount

    return () => clearInterval(intervalId)
  }, [])

  // ---- Arm the disconnect marker whenever a realtime socket drops ----
  // Runs once per gap (the effect only re-fires when transportDisconnected
  // changes) and on mounting mid-gap. Each new gap re-arms the marker and
  // invalidates earlier sweep confirmations.
  useEffect(() => {
    if (transportDisconnected) {
      lastDisconnectTimestamp.current = Date.now()
      apiConfirmedLiveUuids.current.clear()
    }
  }, [transportDisconnected])

  // ---- Reconnect sweep: API-verified pass on transport reconnect ----
  //
  // Both RINGING and CONNECTED deskphone calls are verified against
  // /app/v1/calls/<legUuid>/. The endpoint reports `in-progress` for any live
  // leg (the CallStatus enum has no "ringing" value — a ringing leg and a
  // connected leg are indistinguishable), and a terminal status once the call
  // ends, which is exactly the signal we need.
  //
  // 404 handling: chat-rtm delivers RINGING/answer straight from FreeSwitch,
  // ahead of CDR ingestion, so a 404 on a young leg means "not ingested yet",
  // not "ended". RINGING 404s always defer; a CONNECTED 404 only removes once
  // the leg is older than DESKPHONE_INGESTION_GRACE_MS (old enough to have been
  // ingested) — younger ones defer to the watchdog too.
  // Sweep fires on the "was degraded (either socket down) → now fully back"
  // edge, so a chat-rtm-only reconnect triggers it too, not just SIP.
  const prevDisconnected = useRef(transportDisconnected)
  useEffect(() => {
    const wasDisconnected = prevDisconnected.current
    prevDisconnected.current = transportDisconnected

    if (!(wasDisconnected && !transportDisconnected)) {
      return
    }

    // Reconnected. The disconnect marker deliberately stays set: calls this
    // sweep confirms live get a per-call exemption (apiConfirmedLiveUuids);
    // anything it can't confirm stays on the tighter post-disconnect grace
    // window instead of lingering until the 8h net.
    if (!cleanupEnabledRef.current) return

    const {
      callList: currentCallList,
      removeCall,
      logger,
      track,
    } = refs.current

    const now = Date.now()
    const cancelled = { value: false }
    const abortController = new AbortController()
    const timeoutId = setTimeout(() => abortController.abort(), 10000)

    const deskphoneCalls = Array.from(currentCallList.values()).filter(
      (call) => call.callSource === 'deskphone',
    )

    Promise.all(
      deskphoneCalls.map(
        async (call): Promise<'removed' | 'kept' | 'deferred' | null> => {
          const isRinging =
            call.status === PhoneCallStatus.RINGING ||
            call.status === PhoneCallStatus.INITIAL
          const isConnected = call.status === PhoneCallStatus.CONNECTED
          if (!isRinging && !isConnected) return null

          // Non-UUID callUuid would resolve a different call by primary key —
          // never verify it; defer to the watchdog.
          if (!isLegUuidShaped(call.callUuid)) {
            logger?.debug(
              'Deskphone callUuid is not leg-UUID shaped, deferring to watchdog',
              { callUuid: call.callUuid, status: call.status },
            )
            return 'deferred'
          }

          try {
            const response = await getApiInst().getCallByLegUuid(
              call.callUuid,
              abortController.signal,
            )
            if (cancelled.value) return null

            const is404 = !response.ok && response.status === 404
            // Require a real status string: `undefined !== 'in-progress'` is
            // true, so serializer/schema drift dropping the field would read as
            // "ended" and remove every swept call. Absent → defer to watchdog.
            const apiStatus = response.data?.status
            const endedByStatus =
              response.ok &&
              typeof apiStatus === 'string' &&
              apiStatus !== 'in-progress'

            // A CONNECTED leg answered moments before the blip may not be
            // CDR-ingested yet, so a 404 on a YOUNG call means "not ingested",
            // not "ended" (same race the RINGING 404 path guards). Only treat a
            // 404 as ended once the leg is old enough to have been ingested.
            const callAgeMs = call.startTime
              ? now - call.startTime.getTime()
              : 0
            const connectedEndedBy404 =
              isConnected && is404 && callAgeMs > DESKPHONE_INGESTION_GRACE_MS

            const callDefinitelyEnded = endedByStatus || connectedEndedBy404

            if (callDefinitelyEnded) {
              logger?.warn(
                'API confirmed deskphone call ended after reconnect',
                {
                  callUuid: call.callUuid,
                  status: call.status,
                  apiStatus: response.data?.status ?? `HTTP ${response.status}`,
                  number: call.number,
                },
              )
              track(WebPhoneAnalyticsEvents.OrphanedCallCleanup, {
                layer: 'reconnect_sweep_api_confirmed',
                callSource: 'deskphone',
                callStatus: call.status,
                apiStatus: response.data?.status ?? `HTTP ${response.status}`,
              })
              removeCall(call.callUuid)
              return 'removed'
            } else if (isRinging && is404) {
              logger?.debug(
                'Ringing deskphone leg not found (maybe not ingested), deferring',
                { callUuid: call.callUuid },
              )
              return 'deferred'
            } else if (!response.ok) {
              logger?.debug('API error on sweep, deferring to watchdog', {
                callUuid: call.callUuid,
                httpStatus: response.status,
              })
              return 'deferred'
            } else {
              // Confirmed live: exempt from the post-disconnect grace window
              // (the lost-HANGUP suspicion is resolved; the 8h net still holds).
              apiConfirmedLiveUuids.current.add(call.callUuid)
              logger?.info('API confirmed deskphone call still active', {
                callUuid: call.callUuid,
                status: call.status,
                number: call.number,
              })
              return 'kept'
            }
          } catch {
            if (cancelled.value) return null
            logger?.debug('API verification failed, deferring to watchdog', {
              callUuid: call.callUuid,
            })
            return 'deferred'
          }
        },
      ),
    )
      .then((outcomes) => {
        if (cancelled.value) return
        // Heartbeat: one aggregated event per sweep (not per call — routine
        // chat-rtm token-expiry reconnects would multiply per-call events).
        // Emitting kept/deferred counts gives a ratio so "0 removals" means
        // "nothing stuck", not "feature not running". Removals stay per-call
        // above — they're rare and carry the alarm-worthy detail.
        const keptCount = outcomes.filter((o) => o === 'kept').length
        const deferredCount = outcomes.filter((o) => o === 'deferred').length
        if (keptCount > 0 || deferredCount > 0) {
          track(WebPhoneAnalyticsEvents.OrphanedCallCleanup, {
            layer: 'reconnect_sweep_api_kept',
            callSource: 'deskphone',
            keptCount,
            deferredCount,
          })
        }
      })
      .finally(() => clearTimeout(timeoutId))

    return () => {
      cancelled.value = true
      abortController.abort()
      clearTimeout(timeoutId)
    }
  }, [transportDisconnected])
}
